import React, { useEffect, useState, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const STATUS_FLOW = ['PENDIENTE', 'EN_PRODUCCION', 'REVISION_INTERNA', 'REVISION_CLIENTE', 'APROBADO'];
const STATUS_LABELS: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  EN_PRODUCCION: 'En producción',
  REVISION_INTERNA: 'Revisión interna',
  REVISION_CLIENTE: 'Revisión cliente',
  APROBADO: 'Aprobado',
  SUBIDO: 'Subido'
};
const STATUS_COLORS: Record<string, string> = {
  PENDIENTE: '#64748b',
  EN_PRODUCCION: '#2688c9',
  REVISION_INTERNA: '#2bb0e6',
  REVISION_CLIENTE: '#f59e0b',
  APROBADO: '#10b981',
  SUBIDO: '#059669'
};

const FORMAT_ICONS: Record<string, string> = {
  ESTATICA: '🖼️',
  ANIMACION: '✨',
  VIDEO: '🎬',
  CARRUSEL: '📑'
};

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const isImage = (url: any) => typeof url === 'string' && /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
const isVideo = (url: any) => typeof url === 'string' && /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url);

const parseImageUrls = (urls: any): string[] => {
  if (!urls) return [];
  if (Array.isArray(urls)) return urls.filter(u => typeof u === 'string');
  if (typeof urls === 'string') {
    try {
      const parsed = JSON.parse(urls);
      if (Array.isArray(parsed)) return parsed.filter(u => typeof u === 'string');
    } catch {
      return [urls];
    }
  }
  return [];
};

export const Kanban: React.FC = () => {
  const { user } = useAuth();
  const [pieces, setPieces] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const currentMonthIdx = new Date().getMonth() + 1;
  const [filterMonth, setFilterMonth] = useState<string>('all');
  const [filterBrand, setFilterBrand] = useState<string>('');
  const [filterUser, setFilterUser] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Drag & Drop
  const [draggingPiece, setDraggingPiece] = useState<any>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  // Modales
  const [selected, setSelected] = useState<any>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [uploadingCommentImg, setUploadingCommentImg] = useState(false);
  const commentImgRef = useRef<HTMLInputElement>(null);

  // IA y Edición de Copy
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [isEditingCopy, setIsEditingCopy] = useState(false);
  const [editedCopy, setEditedCopy] = useState('');

  // Audio (Notas de Voz)
  const [isRecording, setIsRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [sendingAudio, setSendingAudio] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<any>(null);

  // Re-subida de archivo
  const [uploadingFile, setUploadingFile] = useState(false);
  const replaceFileRef = useRef<HTMLInputElement>(null);

  // Modal Nueva Pieza
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState({
    title: '',
    brandId: '',
    type: 'ESTATICA',
    format: '1080x1080',
    copy: '',
    status: 'EN_PRODUCCION',
    assignedToId: ''
  });
  const [creatingPiece, setCreatingPiece] = useState(false);

  const isAdmin = ['ADMIN', 'CUENTAS'].includes(user?.role || '');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pRes, bRes, uRes] = await Promise.all([
        api.get('/pieces'),
        api.get('/brands'),
        api.get('/users').catch(() => ({ data: [] }))
      ]);
      setPieces(Array.isArray(pRes.data) ? pRes.data : []);
      setBrands(Array.isArray(bRes.data) ? bRes.data : []);
      setUsers(Array.isArray(uRes.data) ? uRes.data : []);
    } catch {
      toast.error('Error al cargar datos del tablero');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selected?.id) {
      loadComments(selected.id);
      setEditedCopy(selected.copy || '');
      setIsEditingCopy(false);
    } else {
      setComments([]);
    }
  }, [selected?.id]);

  const loadComments = async (pieceId: string) => {
    try {
      const r = await api.get(`/pieces/${pieceId}/comments`);
      setComments(Array.isArray(r.data) ? r.data : []);
    } catch {
      setComments([]);
    }
  };

  const handleStatusChange = async (pieceId: string, newStatus: string) => {
    const prevPieces = [...pieces];
    setPieces(prev => prev.map(p => p.id === pieceId ? { ...p, status: newStatus } : p));
    if (selected?.id === pieceId) {
      setSelected((prev: any) => prev ? { ...prev, status: newStatus } : null);
    }
    try {
      await api.put(`/pieces/${pieceId}/status`, { status: newStatus });
      toast.success(`Movido a ${STATUS_LABELS[newStatus] || newStatus}`);
    } catch {
      setPieces(prevPieces);
      toast.error('Error al actualizar estado');
    }
  };

  // IA Copy Generator
  const handleGenerateAICopy = async () => {
    if (!selected) return;
    setGeneratingCopy(true);
    try {
      const r = await api.post(`/pieces/${selected.id}/generate-copy`);
      if (r.data?.copy) {
        setSelected((prev: any) => ({ ...prev, copy: r.data.copy }));
        setPieces(prev => prev.map(p => p.id === selected.id ? { ...p, copy: r.data.copy } : p));
        setEditedCopy(r.data.copy);
        setIsEditingCopy(false);
        toast.success('✨ Copy generado con éxito por Gemini');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al generar copy con IA');
    } finally {
      setGeneratingCopy(false);
    }
  };

  const handleSaveCopy = async () => {
    if (!selected) return;
    try {
      await api.patch(`/pieces/${selected.id}/copy`, { copy: editedCopy });
      setSelected((prev: any) => ({ ...prev, copy: editedCopy }));
      setPieces(prev => prev.map(p => p.id === selected.id ? { ...p, copy: editedCopy } : p));
      setIsEditingCopy(false);
      toast.success('Copy guardado');
    } catch {
      toast.error('Error al guardar copy');
    }
  };

  // Comentarios
  const handleSendComment = async () => {
    if (!commentText.trim() || !selected) return;
    setSendingComment(true);
    try {
      await api.post(`/pieces/${selected.id}/comment`, { content: commentText.trim() });
      setCommentText('');
      await loadComments(selected.id);
    } catch {
      toast.error('Error al enviar comentario');
    } finally {
      setSendingComment(false);
    }
  };

  const handleCommentImage = async (file: File) => {
    if (!selected) return;
    setUploadingCommentImg(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/uploads', formData);
      await api.post(`/pieces/${selected.id}/comment`, { imageUrls: [uploadRes.data.url] });
      await loadComments(selected.id);
      toast.success('Imagen adjuntada');
    } catch {
      toast.error('Error al subir imagen');
    } finally {
      setUploadingCommentImg(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imgItem = items.find(item => item.type.startsWith('image/'));
    if (imgItem) {
      e.preventDefault();
      const file = imgItem.getAsFile();
      if (file) handleCommentImage(file);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!selected) return;
    try {
      await api.delete(`/pieces/${selected.id}/comment/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comentario eliminado');
    } catch {
      toast.error('Error al eliminar comentario');
    }
  };

  // Audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await sendAudioNote(audioBlob);
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      setIsRecording(true);
      setRecSeconds(0);
      timerRef.current = setInterval(() => setRecSeconds(s => s + 1), 1000);
    } catch {
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
      audioChunksRef.current = [];
    }
  };

  const sendAudioNote = async (blob: Blob) => {
    if (!selected) return;
    setSendingAudio(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'audio_comment.webm');
      const upRes = await api.post('/uploads', formData);
      await api.post(`/pieces/${selected.id}/comment`, { audioUrl: upRes.data.url });
      await loadComments(selected.id);
      toast.success('Nota de voz enviada');
    } catch {
      toast.error('Error al enviar nota de voz');
    } finally {
      setSendingAudio(false);
      setRecSeconds(0);
    }
  };

  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const upRes = await api.post('/uploads', formData);
      await api.patch(`/pieces/${selected.id}/file`, { fileUrl: upRes.data.url });
      setSelected((prev: any) => ({ ...prev, fileUrl: upRes.data.url }));
      setPieces(prev => prev.map(p => p.id === selected.id ? { ...p, fileUrl: upRes.data.url } : p));
      toast.success('Arte actualizado con éxito');
    } catch {
      toast.error('Error al actualizar arte');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleCreatePiece = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newForm.title.trim() || !newForm.brandId) {
      toast.error('Completa título y marca');
      return;
    }
    setCreatingPiece(true);
    try {
      const r = await api.post('/pieces', newForm);
      setPieces(prev => [r.data, ...prev]);
      setShowNewModal(false);
      setNewForm({ title: '', brandId: '', type: 'ESTATICA', format: '1080x1080', copy: '', status: 'EN_PRODUCCION', assignedToId: '' });
      toast.success('Pieza creada en Etapas');
    } catch {
      toast.error('Error al crear pieza');
    } finally {
      setCreatingPiece(false);
    }
  };

  // FILTRADO INTELIGENTE
  const filteredPieces = pieces.filter(p => {
    const pDate = new Date(p.createdAt || p.publishDate || p.updatedAt);

    // 1. Filtro por Mes
    if (filterMonth !== 'all') {
      const pMonth = pDate.getMonth() + 1;
      if (pMonth !== parseInt(filterMonth, 10)) return false;
    }

    // 2. Filtro por Diseñador / Responsable
    if (filterUser) {
      const respId = p.assignedToId || p.request?.assignedToId || p.createdById;
      if (respId !== filterUser) return false;
    }

    // 3. Filtro por Marca
    if (filterBrand && p.brandId !== filterBrand) return false;

    // 4. Buscador
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = (p.title || '').toLowerCase().includes(q);
      const matchCopy = (p.copy || '').toLowerCase().includes(q);
      const matchBrand = (p.brand?.name || '').toLowerCase().includes(q);
      if (!matchTitle && !matchCopy && !matchBrand) return false;
    }

    return true;
  });

  return (
    <div className="zoik-page-container" style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f4f7f6', fontFamily: '"Montserrat", sans-serif' }}>
      <Sidebar />
      <main className="zoik-page-main" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', minWidth: 0 }}>

        {/* TOOLBAR SUPERIOR FIJA */}
        <div style={{
          background: '#ffffff',
          borderBottom: '1px solid rgba(15, 42, 74, 0.08)',
          padding: '14px 28px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(15, 42, 74, 0.03)'
        }}>
          {/* Título y Contador */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ margin: 0, fontSize: '19px', fontWeight: 900, color: '#0f2a4a' }}>
              Etapas & Producción
            </h1>
            <span style={{ fontSize: '11px', fontWeight: 800, background: 'rgba(38,136,201,0.12)', color: '#2688c9', padding: '3px 10px', borderRadius: '12px' }}>
              {filteredPieces.length} piezas
            </span>
          </div>

          {/* FILTROS DINÁMICOS */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {/* Buscador */}
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="🔍 Buscar..."
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(15, 42, 74, 0.12)',
                background: '#f8fafc',
                fontSize: '12px',
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                width: '150px'
              }}
            />

            {/* Filtro Mes */}
            <select
              value={filterMonth}
              onChange={e => setFilterMonth(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(15, 42, 74, 0.12)',
                background: '#f8fafc',
                fontSize: '12px',
                fontWeight: 700,
                color: '#0f2a4a',
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif'
              }}
            >
              <option value="all">📅 Todo el año</option>
              {MONTH_NAMES.map((mName, i) => (
                <option key={i + 1} value={String(i + 1)}>
                  {mName} {i + 1 === currentMonthIdx ? '(Mes Actual)' : ''}
                </option>
              ))}
            </select>

            {/* Filtro Diseñador / Responsable */}
            <select
              value={filterUser}
              onChange={e => setFilterUser(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(15, 42, 74, 0.12)',
                background: '#f8fafc',
                fontSize: '12px',
                fontWeight: 700,
                color: '#0f2a4a',
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                maxWidth: '170px'
              }}
            >
              <option value="">🎨 Todos los Diseñadores</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName?.split(' ')[0]} ({u.role})
                </option>
              ))}
            </select>

            {/* Filtro Marca */}
            <select
              value={filterBrand}
              onChange={e => setFilterBrand(e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(15, 42, 74, 0.12)',
                background: '#f8fafc',
                fontSize: '12px',
                color: '#0f2a4a',
                outline: 'none',
                fontFamily: '"Montserrat", sans-serif',
                maxWidth: '150px'
              }}
            >
              <option value="">Todas las Marcas</option>
              {brands.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>

            {isAdmin && (
              <button
                onClick={() => setShowNewModal(true)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #1e6fa8 0%, #2688c9 100%)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  boxShadow: '0 2px 6px rgba(15, 42, 74, 0.08)'
                }}
              >
                + Nueva Pieza
              </button>
            )}
          </div>
        </div>

        {/* TABLERO KANBAN DE ALTO COMPLETO: SCROLL INDEPENDIENTE POR COLUMNA */}
        <div style={{
          flex: 1,
          minHeight: 0,
          padding: '16px 24px 20px',
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(260px, 1fr))',
          gap: '16px',
          overflowX: 'auto',
          overflowY: 'hidden',
          alignItems: 'stretch'
        }}>
          {STATUS_FLOW.map(status => {
            const colPieces = filteredPieces.filter(p => p.status === status);
            const isOver = dragOverCol === status;
            const colColor = STATUS_COLORS[status] || '#64748b';

            return (
              <div
                key={status}
                onDragOver={(e) => { e.preventDefault(); setDragOverCol(status); }}
                onDragLeave={() => setDragOverCol(null)}
                onDrop={() => {
                  if (draggingPiece && draggingPiece.status !== status) {
                    handleStatusChange(draggingPiece.id, status);
                  }
                  setDraggingPiece(null);
                  setDragOverCol(null);
                }}
                style={{
                  background: isOver ? 'rgba(38, 136, 201, 0.08)' : 'linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%)',
                  backgroundImage: isOver ? 'none' : 'radial-gradient(rgba(15, 42, 74, 0.05) 1px, transparent 1px)',
                  backgroundSize: '16px 16px',
                  borderRadius: '16px',
                  border: isOver ? `2px dashed ${colColor}` : '1px solid rgba(15, 42, 74, 0.08)',
                  boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.8), 0 2px 6px rgba(15, 42, 74, 0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  maxHeight: '100%',
                  overflow: 'hidden'
                }}
              >
                {/* Cabecera Fija de la Columna */}
                <div style={{
                  padding: '12px 16px',
                  borderBottom: `2px solid ${colColor}`,
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(6px)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexShrink: 0
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: colColor }} />
                    <span style={{ fontSize: '13px', fontWeight: 800, color: '#0f2a4a', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      {STATUS_LABELS[status]}
                    </span>
                  </div>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 800,
                    color: colColor,
                    background: `${colColor}18`,
                    padding: '2px 8px',
                    borderRadius: '12px'
                  }}>
                    {colPieces.length}
                  </span>
                </div>

                {/* SCROLL INDEPENDIENTE DENTRO DE CADA COLUMNA */}
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {colPieces.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 10px', color: 'rgba(15,42,74,0.25)', fontSize: '12px', fontWeight: 600 }}>
                      Sin piezas en esta etapa
                    </div>
                  ) : (
                    colPieces.map(piece => {
                      const assignedUser = piece.assignedTo || piece.request?.assignedTo || piece.createdBy;
                      const respFirstName = assignedUser?.firstName || 'Sin';
                      const respLastName = assignedUser?.lastName || 'asignar';
                      const respName = assignedUser ? `${respFirstName} ${respLastName}` : 'Sin asignar';
                      const respAvatar = assignedUser?.avatar;

                      return (
                        <div
                          key={piece.id}
                          draggable
                          onDragStart={() => setDraggingPiece(piece)}
                          onClick={() => setSelected(piece)}
                          style={{
                            background: '#ffffff',
                            borderRadius: '14px',
                            border: '1px solid rgba(15, 42, 74, 0.07)',
                            boxShadow: '0 2px 8px rgba(15, 42, 74, 0.04)',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            transition: 'transform 0.15s, box-shadow 0.15s',
                            position: 'relative'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(15, 42, 74, 0.08)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(15, 42, 74, 0.04)'; }}
                        >
                          {/* Miniatura Visual (Como antes) */}
                          {isImage(piece.fileUrl) ? (
                            <div style={{ width: '100%', height: '120px', background: '#f1f5f9', overflow: 'hidden' }}>
                              <img src={piece.fileUrl} alt={piece.title || 'Arte'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                          ) : isVideo(piece.fileUrl) ? (
                            <div style={{ width: '100%', height: '80px', background: '#0f2a4a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px' }}>
                              🎬
                            </div>
                          ) : null}

                          <div style={{ padding: '12px 14px' }}>
                            {/* Marca y Formato */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 800, color: '#2688c9', textTransform: 'uppercase', letterSpacing: '0.3px', maxWidth: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {piece.brand?.name || 'Zoik Analyst'}
                              </span>
                              <span style={{ fontSize: '10px', color: 'rgba(15, 42, 74, 0.45)', fontWeight: 600 }}>
                                {FORMAT_ICONS[piece.type] || '📄'} {piece.type || 'ESTATICA'}
                              </span>
                            </div>

                            {/* Título de la Pieza */}
                            <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f2a4a', lineHeight: 1.4, marginBottom: '10px' }}>
                              {piece.title || 'Sin título'}
                            </div>

                            {/* Responsable Visible */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              paddingTop: '8px',
                              borderTop: '1px solid rgba(15, 42, 74, 0.05)',
                              marginTop: '4px'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} title={`Trabajando en esto: ${respName}`}>
                                {respAvatar ? (
                                  <img src={respAvatar} alt={respName} style={{ width: '22px', height: '22px', borderRadius: '50%', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '50%',
                                    background: '#0f2a4a',
                                    color: 'white',
                                    fontSize: '9px',
                                    fontWeight: 800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    {respFirstName[0] || 'U'}
                                  </div>
                                )}
                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#0f2a4a', maxWidth: '110px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {respFirstName}
                                </span>
                              </div>

                              <div style={{ fontSize: '11px', color: 'rgba(15, 42, 74, 0.4)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                💬 {piece.comments?.length || 0}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* MODAL DE DETALLE Y RETROALIMENTACIÓN */}
        {selected && (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(10, 25, 47, 0.8)',
              backdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '24px'
            }}
          >
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              width: '100%',
              maxWidth: '1050px',
              height: '88vh',
              maxHeight: '850px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Header Modal */}
              <div style={{
                padding: '16px 28px',
                borderBottom: '1px solid rgba(15,42,74,0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc',
                flexShrink: 0
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#2688c9', textTransform: 'uppercase' }}>
                      {selected.brand?.name || 'Zoik Analyst'}
                    </span>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: 800,
                      background: `${STATUS_COLORS[selected.status] || '#64748b'}20`,
                      color: STATUS_COLORS[selected.status] || '#64748b'
                    }}>
                      {STATUS_LABELS[selected.status] || selected.status}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f2a4a' }}>
                    {selected.title || 'Sin título'}
                  </h2>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <select
                    value={selected.status}
                    onChange={e => handleStatusChange(selected.id, e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid rgba(15,42,74,0.15)',
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#0f2a4a',
                      background: 'white',
                      fontFamily: '"Montserrat", sans-serif'
                    }}
                  >
                    {STATUS_FLOW.map(s => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>

                  <button
                    onClick={() => setSelected(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '22px',
                      color: 'rgba(15,42,74,0.5)',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      lineHeight: 1
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Cuerpo Modal */}
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1.2fr 1fr', overflow: 'hidden' }}>

                {/* Columna Izquierda: Arte y Copy */}
                <div style={{ padding: '24px 28px', overflowY: 'auto', borderRight: '1px solid rgba(15,42,74,0.08)' }}>
                  {/* Arte Centrado */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.4)', textTransform: 'uppercase', marginBottom: '8px' }}>
                      Arte / Entregable
                    </div>
                    {isImage(selected.fileUrl) ? (
                      <div style={{ borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(15,42,74,0.1)', background: '#0a192f', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img
                          onClick={() => setLightboxUrl(selected.fileUrl)}
                          src={selected.fileUrl}
                          alt={selected.title || 'Arte'}
                          style={{ cursor: 'zoom-in', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: 'auto' }}
                        />
                      </div>
                    ) : isVideo(selected.fileUrl) ? (
                      <video src={selected.fileUrl} controls style={{ width: '100%', borderRadius: '14px', maxHeight: '300px', background: '#000' }} />
                    ) : (
                      <div style={{ padding: '36px', borderRadius: '14px', background: '#f8fafc', border: '2px dashed rgba(15,42,74,0.15)', textAlign: 'center', color: 'rgba(15,42,74,0.4)' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>Sin archivo adjunto aún</div>
                      </div>
                    )}

                    {/* Botones de Archivo */}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      {selected.fileUrl && (
                        <button
                          type="button"
                          onClick={() => setLightboxUrl(selected.fileUrl)}
                          style={{
                            padding: '7px 14px',
                            borderRadius: '8px',
                            border: '1px solid rgba(15,42,74,0.15)',
                            background: 'white',
                            color: '#0f2a4a',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          🔍 Ampliar Arte
                        </button>
                      )}
                      <button
                        onClick={() => replaceFileRef.current?.click()}
                        disabled={uploadingFile}
                        style={{
                          padding: '7px 14px',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#2688c9',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: '"Montserrat", sans-serif'
                        }}
                      >
                        {uploadingFile ? 'Subiendo...' : '🔄 Reemplazar Arte'}
                      </button>
                      <input
                        ref={replaceFileRef}
                        type="file"
                        accept="image/*,video/*"
                        style={{ display: 'none' }}
                        onChange={handleReplaceFile}
                      />
                    </div>
                  </div>

                  {/* Copy / Contenido con IA Gemini */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: 'rgba(15,42,74,0.4)', textTransform: 'uppercase' }}>
                        Copy / Contenido
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {selected.copy && !isEditingCopy && (
                          <>
                            <button
                              onClick={() => { setEditedCopy(selected.copy); setIsEditingCopy(true); }}
                              style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                            >
                              ✏️ Editar
                            </button>
                            <button
                              onClick={() => { navigator.clipboard.writeText(selected.copy); toast.success('Copy copiado al portapapeles'); }}
                              style={{ background: 'none', border: 'none', color: '#2688c9', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                            >
                              📋 Copiar
                            </button>
                          </>
                        )}
                        <button
                          onClick={handleGenerateAICopy}
                          disabled={generatingCopy}
                          style={{
                            padding: '5px 12px',
                            borderRadius: '8px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #2688c9 100%)',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: 800,
                            cursor: generatingCopy ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
                          }}
                        >
                          {generatingCopy ? '✨ Generando con IA...' : '✨ Generar con IA'}
                        </button>
                      </div>
                    </div>

                    {isEditingCopy ? (
                      <div>
                        <textarea
                          value={editedCopy}
                          onChange={e => setEditedCopy(e.target.value)}
                          rows={6}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '12px',
                            border: '1px solid #2688c9',
                            fontSize: '13px',
                            fontFamily: '"Montserrat", sans-serif',
                            lineHeight: 1.6,
                            boxSizing: 'border-box' as any,
                            outline: 'none',
                            marginBottom: '8px'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => setIsEditingCopy(false)}
                            style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={handleSaveCopy}
                            style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#2688c9', color: 'white', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                          >
                            💾 Guardar Copy
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        padding: '14px',
                        borderRadius: '12px',
                        background: '#f8fafc',
                        border: '1px solid rgba(15,42,74,0.08)',
                        fontSize: '13px',
                        color: '#0f2a4a',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        maxHeight: '160px',
                        overflowY: 'auto'
                      }}>
                        {selected.copy || 'Sin copy registrado para esta pieza. Haz clic en "Generar con IA" para crear uno automáticamente.'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Columna Derecha: Retroalimentación */}
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fafbfc' }}>
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(15,42,74,0.06)', fontWeight: 800, fontSize: '13px', color: '#0f2a4a', flexShrink: 0 }}>
                    💬 Retroalimentación & Ajustes ({comments.length})
                  </div>

                  <div style={{ flex: 1, padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {comments.length === 0 ? (
                      <div style={{ textAlign: 'center', color: 'rgba(15,42,74,0.3)', fontSize: '13px', padding: '40px 0' }}>
                        No hay comentarios todavía.<br />Sé el primero en retroalimentar.
                      </div>
                    ) : (
                      comments.map(c => {
                        const authorName = c.user ? `${c.user.firstName || ''} ${c.user.lastName || ''}`.trim() : 'Usuario';
                        const authorInit = c.user?.firstName?.[0] || 'U';
                        const imgList = parseImageUrls(c.imageUrls);

                        return (
                          <div key={c.id} style={{ background: 'white', borderRadius: '12px', padding: '12px', border: '1px solid rgba(15,42,74,0.06)', boxShadow: '0 1px 4px rgba(0,0,0,0.02)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#2688c9', color: 'white', fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {authorInit}
                                </div>
                                <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>
                                  {authorName}
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '10px', color: 'rgba(15,42,74,0.35)' }}>
                                  {c.createdAt ? new Date(c.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                                {(user?.id === c.userId || isAdmin) && (
                                  <button onClick={() => handleDeleteComment(c.id)} style={{ background: 'none', border: 'none', color: 'rgba(15,42,74,0.25)', cursor: 'pointer', fontSize: '14px' }}>×</button>
                                )}
                              </div>
                            </div>

                            {c.content && (
                              <div style={{ fontSize: '12px', color: '#0f2a4a', lineHeight: 1.5 }}>
                                {c.content}
                              </div>
                            )}

                            {imgList.map((url: string, i: number) => (
                              <div key={i} onClick={() => setLightboxUrl(url)} style={{ cursor: 'zoom-in', display: 'inline-block', marginTop: '6px' }}>
                                <img src={url} alt="Captura" style={{ maxWidth: '100%', borderRadius: '8px', maxHeight: '180px', objectFit: 'cover' }} />
                              </div>
                            ))}

                            {c.audioUrl && (
                              <div style={{ marginTop: '8px' }}>
                                <audio src={c.audioUrl} controls style={{ width: '100%', height: '36px' }} />
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(15,42,74,0.08)', background: 'white', flexShrink: 0 }}>
                    {isRecording ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(239,68,68,0.08)', padding: '10px 14px', borderRadius: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', fontWeight: 700, fontSize: '13px' }}>
                          <span>🔴</span> Grabando: {recSeconds}s
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={cancelRecording} style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', fontSize: '11px', cursor: 'pointer' }}>Cancelar</button>
                          <button onClick={stopRecording} disabled={sendingAudio} style={{ padding: '5px 12px', borderRadius: '6px', border: 'none', background: '#ef4444', color: 'white', fontWeight: 700, fontSize: '11px', cursor: 'pointer' }}>{sendingAudio ? 'Enviando...' : 'Enviar Audio ✓'}</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          value={commentText}
                          onChange={e => setCommentText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSendComment(); } }}
                          onPaste={handlePaste}
                          placeholder="Escribe una nota o pega captura (Ctrl+V)..."
                          style={{
                            flex: 1,
                            padding: '10px 14px',
                            borderRadius: '10px',
                            border: '1px solid rgba(15,42,74,0.12)',
                            background: '#f8fafc',
                            fontSize: '12px',
                            outline: 'none',
                            fontFamily: '"Montserrat", sans-serif'
                          }}
                        />

                        <button
                          type="button"
                          onClick={() => commentImgRef.current?.click()}
                          disabled={uploadingCommentImg}
                          style={{ padding: '9px 12px', borderRadius: '10px', border: '1px solid rgba(15,42,74,0.12)', background: '#f8fafc', cursor: 'pointer', fontSize: '14px' }}
                          title="Adjuntar imagen"
                        >
                          📷
                        </button>
                        <input ref={commentImgRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) handleCommentImage(e.target.files[0]); }} />

                        <button
                          type="button"
                          onClick={startRecording}
                          style={{ padding: '9px 12px', borderRadius: '10px', border: '1px solid rgba(15,42,74,0.12)', background: '#f8fafc', cursor: 'pointer', fontSize: '14px' }}
                          title="Grabar nota de voz"
                        >
                          🎙️
                        </button>

                        <button
                          type="button"
                          onClick={handleSendComment}
                          disabled={sendingComment || !commentText.trim()}
                          style={{
                            padding: '9px 16px',
                            borderRadius: '10px',
                            border: 'none',
                            background: '#2688c9',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '12px',
                            cursor: 'pointer',
                            opacity: sendingComment || !commentText.trim() ? 0.5 : 1
                          }}
                        >
                          Enviar
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* MODAL LIGHTBOX EN PANTALLA COMPLETA */}
        {lightboxUrl && (
          <div
            onClick={() => setLightboxUrl(null)}
            style={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(5, 13, 26, 0.92)',
              backdropFilter: 'blur(14px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000,
              padding: '30px'
            }}
          >
            <div onClick={e => e.stopPropagation()} style={{ position: 'relative', maxWidth: '92vw', maxHeight: '92vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <button
                onClick={() => setLightboxUrl(null)}
                style={{
                  position: 'absolute',
                  top: '-45px',
                  right: '0px',
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  fontSize: '18px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>

              {isImage(lightboxUrl) ? (
                <img src={lightboxUrl} alt="Vista previa original" style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} />
              ) : isVideo(lightboxUrl) ? (
                <video src={lightboxUrl} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} />
              ) : (
                <div style={{ background: 'white', padding: '40px', borderRadius: '16px' }}>
                  <a href={lightboxUrl} target="_blank" rel="noreferrer" style={{ color: '#2688c9', fontWeight: 700 }}>Descargar Archivo</a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* MODAL CREAR NUEVA PIEZA */}
        {showNewModal && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(10,25,47,0.7)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
              <h2 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 900, color: '#0f2a4a' }}>+ Crear Nueva Pieza en Etapas</h2>
              <form onSubmit={handleCreatePiece}>
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', marginBottom: '6px' }}>Título del Entregable</label>
                  <input value={newForm.title} onChange={e => setNewForm(f => ({ ...f, title: e.target.value }))} placeholder="Ej: Reel Lanzamiento Producto" required style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' as any }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', marginBottom: '6px' }}>Marca</label>
                    <select value={newForm.brandId} onChange={e => setNewForm(f => ({ ...f, brandId: e.target.value }))} required style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' as any }}>
                      <option value="">Seleccionar...</option>
                      {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', marginBottom: '6px' }}>Formato</label>
                    <select value={newForm.type} onChange={e => setNewForm(f => ({ ...f, type: e.target.value }))} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' as any }}>
                      <option value="ESTATICA">🖼️ Estática</option>
                      <option value="CARRUSEL">📑 Carrusel</option>
                      <option value="VIDEO">🎬 Video / Reel</option>
                      <option value="ANIMACION">✨ Animación</option>
                    </select>
                  </div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', marginBottom: '6px' }}>Asignar Responsable</label>
                  <select value={newForm.assignedToId} onChange={e => setNewForm(f => ({ ...f, assignedToId: e.target.value }))} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' as any }}>
                    <option value="">Sin asignar (Opcional)</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.firstName} {u.lastName} ({u.role})</option>)}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: 'rgba(15,42,74,0.6)', textTransform: 'uppercase', marginBottom: '6px' }}>Copy Inicial (Opcional)</label>
                  <textarea value={newForm.copy} onChange={e => setNewForm(f => ({ ...f, copy: e.target.value }))} placeholder="Texto o instrucciones de la pieza..." rows={3} style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', resize: 'vertical' as any, boxSizing: 'border-box' as any }} />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={() => setShowNewModal(false)} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: '1px solid #cbd5e1', background: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>Cancelar</button>
                  <button type="submit" disabled={creatingPiece} style={{ flex: 1, padding: '11px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#1e6fa8,#2688c9)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>{creatingPiece ? 'Creando...' : 'Crear Pieza'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default Kanban;
